/* POWPAX product pages (cinematic version) — gate, nav, quantity, cart, reveals.
   Deliberately no GSAP and no Lenis: the homepage is the spectacle, this page is the checkout.
   The one motion flourish (the slam word drifting) is scroll-driven CSS. */
(() => {
  const root = document.documentElement;

  /* ---- motion preference: same key and semantics as the homepage ---- */
  const motionOff = localStorage.getItem('powpax-motion') === 'off'
    || (localStorage.getItem('powpax-motion') !== 'on'
        && matchMedia('(prefers-reduced-motion: reduce)').matches);

  const toggle = document.getElementById('motionToggle');
  if (toggle) {
    toggle.textContent = motionOff ? 'MOTION: OFF' : 'MOTION: ON';
    toggle.setAttribute('aria-pressed', String(!motionOff));
    toggle.addEventListener('click', () => {
      localStorage.setItem('powpax-motion', motionOff ? 'on' : 'off');
      location.reload();
    });
  }

  /* ---- age gate: shares the homepage session key, so it asks once per visit ---- */
  const gate = document.getElementById('gate');
  if (gate) {
    if (sessionStorage.getItem('powpax-age') === '1') gate.remove();
    else {
      const yes = document.getElementById('gateYes');
      yes.focus();
      gate.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        const f = gate.querySelectorAll('button, a');
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      yes.addEventListener('click', () => {
        sessionStorage.setItem('powpax-age', '1');
        gate.remove();
        const h1 = document.querySelector('.pdp-slam h1');
        if (h1) { h1.setAttribute('tabindex', '-1'); h1.focus({ preventScroll: true }); }
      });
    }
  }

  /* ---- nav: goes to the cream blur once the slam is behind you ---- */
  const nav = document.getElementById('nav');
  if (nav) {
    const onScroll = () => nav.classList.toggle('scrolled', scrollY > innerHeight * 0.7);
    addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---- quantity ---- */
  const qtyInput = document.getElementById('pdpQty');
  const clamp = (n) => Math.min(99, Math.max(1, n || 1));
  if (qtyInput) {
    document.querySelectorAll('[data-qty]').forEach((b) => b.addEventListener('click', () => {
      qtyInput.value = clamp(parseInt(qtyInput.value, 10) + Number(b.dataset.qty));
    }));
    qtyInput.addEventListener('change', () => { qtyInput.value = clamp(parseInt(qtyInput.value, 10)); });
  }

  /* Add to Cart lives in assets/cart.js now — it owns [data-add] buttons, reads this page's
     #pdpQty for the quantity, and hands off to Shopify once at checkout. */

  /* ---- options + gallery (client 9/11: Cann-style picker) ----
     The page ships every variant as JSON. Picking a pack re-prices in place; picking a flavor
     reloads on ?variant= because the whole page below the fold (panel, art, copy) is keyed by
     flavor and is rendered by Liquid, not here. */
  const dataEl = document.getElementById('pdpData');
  const form = document.getElementById('pdpForm');
  let D = null;
  if (dataEl && form) { try { D = JSON.parse(dataEl.textContent); } catch (e) { D = null; } }
  if (D && D.variants && D.variants.length) {
    const money = (cents) => {
      const n = cents / 100;
      return String(D.money || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
        if (k === 'amount_no_decimals') return Math.round(n).toLocaleString('en-US');
        if (k === 'amount_with_comma_separator') return n.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        if (k === 'amount_no_decimals_with_comma_separator') return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }).replace(/<[^>]+>/g, '');
    };
    const radios = [...form.querySelectorAll('.pdp-opt input[type=radio]')];
    const nOpts = D.variants[0].options.length;
    const chosen = () => { const o = new Array(nOpts).fill(undefined); radios.forEach((r) => { if (r.checked) o[+r.dataset.idx] = r.value; }); return o; };
    const fits = (v, opts, skip) => v.options.every((val, i) => i === skip || opts[i] === undefined || opts[i] === val);
    const countOf = (val) => { const m = String(val || '').match(/\d+/); return m ? +m[0] : 0; };
    const $ = (id) => document.getElementById(id);
    const priceEl = $('pdpPrice'), compareEl = $('pdpCompare'), unitEl = $('pdpUnit'), idEl = $('pdpVariantId'), flavorVal = $('pdpFlavorVal');
    const addBtns = [...document.querySelectorAll('[data-add][data-variant]')];
    const barPrice = document.querySelector('.pdp-bar-price');
    const mainImg = $('pdpMainImg');
    const thumbs = [...document.querySelectorAll('.pdp-thumb')];

    function showImage(id, src, srcset, w, h, alt) {
      if (!mainImg || !src) return;
      mainImg.src = src;
      if (srcset) mainImg.srcset = srcset; else mainImg.removeAttribute('srcset');
      if (w && h) { mainImg.width = w; mainImg.height = h; }
      if (alt !== undefined) mainImg.alt = alt;
      thumbs.forEach((t) => t.classList.toggle('is-on', String(t.dataset.id) === String(id)));
    }
    thumbs.forEach((t) => t.addEventListener('click', () => showImage(t.dataset.id, t.dataset.src, t.dataset.srcset, t.dataset.w, t.dataset.h, t.dataset.alt)));

    function paint(changedIdx) {
      const opts = chosen();
      const v = D.variants.find((x) => fits(x, opts, -1));
      radios.forEach((r) => {
        const idx = +r.dataset.idx;
        const label = r.closest('.pdp-opt');
        const cands = D.variants.filter((x) => x.options[idx] === r.value && fits(x, opts, idx));
        label.classList.toggle('is-none', !cands.length);
        label.classList.toggle('is-out', cands.length > 0 && !cands.some((x) => x.available));
        label.classList.toggle('is-on', r.checked);
        if (idx === D.packIdx) {
          const priced = cands.find((x) => x.available) || cands[0];
          const pe = label.querySelector('[data-pack-price]');
          if (pe) pe.textContent = priced ? money(priced.price) : '';
          const c = countOf(r.value);
          label.dataset.unit = priced && c ? String(priced.price / c) : '';
        }
      });
      /* per-stick savings against the smallest pack, so the bigger packs make their own case */
      if (D.packIdx >= 0) {
        const packs = radios.filter((r) => +r.dataset.idx === D.packIdx).map((r) => r.closest('.pdp-opt')).filter((l) => l.dataset.unit);
        const base = packs.reduce((b, l) => (!b || countOf(l.querySelector('input').value) < countOf(b.querySelector('input').value)) ? l : b, null);
        packs.forEach((l) => {
          let s = l.querySelector('.pdp-opt-save');
          const pct = base && l !== base ? Math.round((1 - Number(l.dataset.unit) / Number(base.dataset.unit)) * 100) : 0;
          if (pct >= 3) {
            if (!s) { s = document.createElement('span'); s.className = 'pdp-opt-save'; l.append(s); }
            s.textContent = `Save ${pct}%`;
          } else if (s) s.remove();
        });
      }
      if (!v) {
        addBtns.forEach((b) => { b.disabled = true; b.textContent = 'Unavailable'; });
        return;
      }
      const count = (D.parseCount && D.packIdx >= 0 && countOf(v.options[D.packIdx])) || D.count;
      if (priceEl) priceEl.textContent = money(v.price);
      if (compareEl) {
        if (v.compare > v.price) { compareEl.textContent = money(v.compare); compareEl.hidden = false; }
        else compareEl.hidden = true;
      }
      if (unitEl) unitEl.textContent = `${count} ${D.units} · ${money(Math.round(v.price / count))} ${D.unit === 'stick' ? 'a stick' : 'each'}`;
      if (barPrice) barPrice.textContent = `${money(v.price)} · ${count} ${D.units}`;
      if (idEl) idEl.value = v.id;
      addBtns.forEach((b) => { b.dataset.variant = v.id; b.disabled = !v.available; b.textContent = v.available ? 'Add to Cart' : 'Sold out'; });
      if (flavorVal && D.flavorIdx >= 0) flavorVal.textContent = v.options[D.flavorIdx];
      if (v.image && !(document.getElementById('pdpGallery') || {}).dataset?.lead) showImage(v.image.id, v.image.src, v.image.srcset, v.image.w, v.image.h);
      const url = new URL(location.href);
      url.searchParams.set('variant', v.id);
      if (changedIdx === D.flavorIdx && D.flavorIdx >= 0) { location.assign(url); return; }
      history.replaceState(null, '', url);
    }
    radios.forEach((r) => r.addEventListener('change', () => paint(+r.dataset.idx)));
    paint(-1);
  }

  /* ---- sticky buy bar on phones, once the real button is gone ---- */
  const bar = document.getElementById('pdpBar');
  const anchor = document.querySelector('.pdp-cart');
  if (bar && anchor && 'IntersectionObserver' in window) {
    new IntersectionObserver(([e]) => bar.classList.toggle('is-in', !e.isIntersecting), { rootMargin: '-40px 0px 0px 0px' })
      .observe(anchor);
  }

  /* ---- reveals ---- */
  const reveals = [...document.querySelectorAll('.reveal')];
  if (!motionOff && reveals.length && 'IntersectionObserver' in window) {
    root.classList.add('pdp-motion');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px' });
    reveals.forEach((el) => {
      /* anything already on screen at load shows immediately — never trap content at opacity 0 */
      if (el.getBoundingClientRect().top < innerHeight * 0.9) el.classList.add('is-in');
      else io.observe(el);
    });
  }
})();
